import { Stack, Select, MenuItem, type SelectChangeEvent } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import { dayjs } from '@/utils/dayjs';

import { PrevNextBrowseArrows } from '../prevNextBrowseArrows';

interface MonthSelectorProps {
  months: string[]; // reverse list of months
  onMonthChange: (date: string) => void;
}

export function MonthSelector(props: MonthSelectorProps) {
  const monthsMapper = useMemo(() => {
    return props.months.map((month) => ({
      origin: month,
      target: dayjs(month).format('MMM YYYY'),
    }));
  }, [props.months]);
  const months = useMemo(
    () => monthsMapper.map((month) => month.target),
    [monthsMapper]
  );

  const onMonthChange = (monthStr: string) => {
    const newMonthStr = monthsMapper.find(
      (month) => month.target === monthStr
    )?.origin;
    if (!newMonthStr) {
      throw new Error(
        'Could not select month. Month not exists in original list'
      );
    }
    props.onMonthChange(newMonthStr);
  };

  const [selected, setSelected] = useState<string | undefined>(months[0] || '');
  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  useEffect(() => {
    if (!months.length) {
      return;
    }

    setSelected(months[0]);
    setSelectedIndex(0);
  }, [months]);

  useEffect(() => {
    setSelectedMonth(selectedIndex);
    // lint expecting to add setSelectedMonth.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex, months]);

  const setSelectedMonth = (monthIndex: number) => {
    if (!months.length) {
      return;
    }

    setSelected(months[monthIndex]);
    onMonthChange(months[monthIndex]);
  };

  const onPreviousMonth = () => {
    const newIndex = selectedIndex + 1;
    setSelectedIndex(newIndex);
    setSelectedMonth(newIndex);
  };

  const onNextMonth = () => {
    const newIndex = selectedIndex - 1;
    setSelectedIndex(newIndex);
    setSelectedMonth(newIndex);
  };

  const onSelectMonth = (e: SelectChangeEvent) => {
    setSelected(e.target.value);
    onMonthChange(e.target.value);
    setSelectedIndex(months.indexOf(e.target.value));
  };

  if (!months.length) {
    return null;
  }

  return (
    <Stack
      direction='row'
      alignItems={'center'}
      justifyContent='space-between'
      margin={'16px 40px'}
    >
      <Select className='w-[140px]' value={selected} onChange={onSelectMonth}>
        {months.map((month) => {
          return (
            <MenuItem key={month} value={month}>
              {month}
            </MenuItem>
          );
        })}
      </Select>
      <PrevNextBrowseArrows
        next={{
          onClick: onNextMonth,
          disabled: selectedIndex === 0,
        }}
        previous={{
          onClick: onPreviousMonth,
          disabled: selectedIndex === months.length - 1,
        }}
      />
    </Stack>
  );
}
